/* eslint-disable react-hooks/set-state-in-render --
 * v77.99.77: Snapshot export menu derives custom-date defaults from
 * props during render. Intentional and safe.
 */
// v77.99.20 — Snapshot export range menu for /expenses.
// v77.99.21 — Adds per-child scope picker (sourced from the canonical
//             ChildScopeContext) and School Year preset.
//
// Replaces the single "Snapshot (1-pager)" button with a dropdown
// that lets the user pick:
//   • A SCOPE — "All children" or one specific child (filtered to
//     the children the user is actually permitted to see, via the
//     canonical `useChildScope().children` list).
//   • A RANGE — current month, previous month, year to date, school
//     year (1 Sep → 30 Jun), last 12 months, or a custom date range.
//
// Why both controls live inside the dropdown (not separate buttons):
//   • Per-child exports are the most-requested workflow for blended
//     families and child-specific solicitor folders. They need to
//     be one click away, not gated behind the topbar child switch.
//   • Keeping scope + range together in one menu means there's only
//     ONE place users learn the export model, and exactly ONE
//     network call per export.
//
// Per-child scope picker fully respects child-level permissions:
//   • Source: `useChildScope().children` — server-filtered to the
//     children the user may see.
//   • If the user has access to zero children → the picker hides
//     and only "All children" is shown.
//   • If the user has access to exactly one child → the picker is
//     hidden as redundant (the parent will just use "All children",
//     which is implicitly per-child anyway).
//   • Server-side enforcement remains the authority. If the user's
//     permissions change mid-session the backend returns 404 and
//     the toast surfaces the error.
//
// PDF layout itself is unchanged — only the input that drives its
// `from`/`to`/`child_id`/`range_label` query params.
//
// Filename is computed client-side using structured naming so a
// solicitor's folder organises naturally:
//   coparentconsole_expenses_<preset>_<scope>_<year>.pdf
// e.g.:
//   coparentconsole_expenses_ytd_allchildren_2026.pdf
//   coparentconsole_expenses_schoolyear_mia_2026.pdf
//   coparentconsole_expenses_custom_noah_2026.pdf

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { FileText, ChevronDown, Mail } from "lucide-react";
import { downloadFile } from "@/lib/downloadFile";
import { API } from "@/lib/api";
import { SNAPSHOT_PRESETS, resolvePreset } from "@/lib/snapshotPresets";
import { useChildScope } from "@/context/ChildScopeContext";
import { SnapshotEmailDialog } from "@/components/expenses/SnapshotEmailDialog";
import { toast } from "sonner";

/** Slugify a string for safe use in a filename. */
function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 32) || "x";
}

/** Pick the "year" component of a structured filename — uses the end
 *  date of the export window so the filename matches the time period
 *  it covers (not when it was downloaded). */
function yearForFilename({ to, from }, now = new Date()) {
  const endIso = to || from;
  if (endIso && /^\d{4}/.test(endIso)) return endIso.slice(0, 4);
  return String(now.getFullYear());
}

/** Build the snapshot URL + structured filename for the given window. */
function buildDownload({ presetId, from, to, label, childId, childName }) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (childId) params.set("child_id", childId);
  if (label) params.set("range_label", label);
  const qs = params.toString();
  const url = `${API}/expenses/snapshot/pdf${qs ? `?${qs}` : ""}`;

  // Structured filename (matches the format the user requested):
  //   coparentconsole_expenses_<preset>_<scope>_<year>.pdf
  // The backend still computes its own privacy-safe filename for the
  // Content-Disposition header; this one is the suggested name we
  // pass to downloadFile so the user's Downloads folder is clean.
  const presetSlug = ({
    "current-month": "thismonth",
    "previous-month": "lastmonth",
    "year-to-date": "ytd",
    "school-year": "schoolyear",
    "last-12-months": "last12months",
    "custom": "custom",
  })[presetId] || slugify(presetId);
  const scopeSlug = childId ? slugify(childName || "child") : "allchildren";
  const year = yearForFilename({ from, to });
  const fname = `coparentconsole_expenses_${presetSlug}_${scopeSlug}_${year}.pdf`;
  return { url, fname };
}

export function SnapshotExportMenu({ childFilter }) {
  const { children: visibleChildren, selectedChildId } = useChildScope();

  // Local scope picker default: prefer the active global child scope
  // (so a user who's already drilled into one child sees that child
  // selected in the dropdown) and fall back to the legacy `childFilter`
  // prop. Either way, "All children" stays as the master default.
  const initialScope =
    (selectedChildId && selectedChildId !== "all" && selectedChildId)
    || (childFilter && childFilter !== "all" ? childFilter : "all");

  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(initialScope);
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);

  // Re-sync the picker's default whenever the parent's child filter
  // changes (e.g. user picks a child in the topbar then opens the
  // snapshot menu).
  useMemo(() => {
    setScope(initialScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScope]);

  // The scope picker is only worth showing when there's a real choice
  // (2+ visible children). One child or zero visible → "All children"
  // is the only sensible option.
  const showScopePicker = (visibleChildren?.length || 0) >= 2;

  const scopeChild = scope !== "all"
    ? (visibleChildren || []).find((c) => c.id === scope)
    : null;

  const triggerDownload = ({ presetId, from, to, label }) => {
    const { url, fname } = buildDownload({
      presetId, from, to, label,
      childId: scope !== "all" ? scope : null,
      childName: scopeChild?.name,
    });
    downloadFile(url, fname, {
      label: "Generating snapshot",
      successMessage: "Snapshot ready",
    });
  };

  const handlePreset = (presetId) => {
    if (presetId === "custom") {
      setCustomOpen(true);
      return;
    }
    const resolved = resolvePreset(presetId);
    if (!resolved) return;
    triggerDownload({ presetId, ...resolved });
  };

  const handleCustomSubmit = () => {
    if (!customFrom || !customTo) {
      toast.error("Pick both a start and end date.");
      return;
    }
    if (customFrom > customTo) {
      toast.error("Start date must be on or before end date.");
      return;
    }
    triggerDownload({
      presetId: "custom",
      from: customFrom,
      to: customTo,
      label: "Custom range",
    });
    setCustomOpen(false);
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="border-[#D9E1E3] rounded-sm"
            data-testid="expense-export-snapshot"
          >
            <FileText className="w-4 h-4 mr-2" />
            Snapshot (1-pager)
            <ChevronDown className="w-3 h-3 ml-2 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-72"
          data-testid="expense-snapshot-menu"
        >
          {showScopePicker && (
            <>
              <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A]">
                Scope
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup value={scope} onValueChange={setScope}>
                <DropdownMenuRadioItem
                  value="all"
                  data-testid="expense-snapshot-scope-all"
                  onSelect={(e) => e.preventDefault()}
                >
                  All children
                </DropdownMenuRadioItem>
                {(visibleChildren || []).map((c) => (
                  <DropdownMenuRadioItem
                    key={c.id}
                    value={c.id}
                    data-testid={`expense-snapshot-scope-${c.id}`}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.name}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A]">
            Range
          </DropdownMenuLabel>
          {SNAPSHOT_PRESETS.map((preset) => (
            <DropdownMenuItem
              key={preset.id}
              onSelect={() => handlePreset(preset.id)}
              data-testid={`expense-snapshot-preset-${preset.id}`}
              className="flex-col items-start gap-0.5 py-2"
            >
              <span className="text-sm font-medium">
                {preset.label}
              </span>
              <span className="text-xs text-muted-foreground">
                {preset.description}
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => setEmailOpen(true)}
            data-testid="expense-snapshot-email-trigger"
            className="py-2"
          >
            <Mail className="w-4 h-4 mr-2 opacity-70" />
            <span className="text-sm font-medium">Email to solicitor…</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SnapshotEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        scopeChildId={scope}
        scopeChildName={scopeChild?.name}
      />

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="expense-snapshot-custom-dialog"
        >
          <DialogHeader>
            <DialogTitle>Custom date range</DialogTitle>
            <DialogDescription className="text-[#6F828A]">
              Pick any start and end date. The snapshot recalculates
              totals, settlements and category splits for the window
              you choose.
              {scopeChild && (
                <span className="block mt-1 text-[#0B2E32]">
                  Exporting for <b>{scopeChild.name}</b> only.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="snap-from" className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">
                From
              </Label>
              <Input
                id="snap-from"
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="border-[#D9E1E3] rounded-sm mt-1"
                data-testid="expense-snapshot-custom-from"
              />
            </div>
            <div>
              <Label htmlFor="snap-to" className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">
                To
              </Label>
              <Input
                id="snap-to"
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="border-[#D9E1E3] rounded-sm mt-1"
                data-testid="expense-snapshot-custom-to"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCustomOpen(false)}
              className="border-[#D9E1E3] rounded-sm"
              data-testid="expense-snapshot-custom-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCustomSubmit}
              disabled={!customFrom || !customTo || customFrom > customTo}
              className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
              data-testid="expense-snapshot-custom-generate"
            >
              Generate snapshot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SnapshotExportMenu;
